/**
 * SECCIÓN G2 — Panel lateral fijo de capacidades para Citas de Almacén.
 * Ancho fijo 220px. Consume /api/warehouse-status/summary.
 * Auto-refresh cada 5 min. Botón ↻ manual.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { RefreshCw, AlertTriangle } from "lucide-react";

const TYPE_META = {
  rp:  { color: "#FF6D00", label: "RP",  full: "Almacén RP"  },
  rme: { color: "#1565C0", label: "RME", full: "Almacén RME" },
  rsu: { color: "#2E7D32", label: "RSU", full: "Almacén RSU" },
};

const STATUS = {
  green:  { label: "DISPONIBLE", bg: "rgba(0,200,83,0.18)",  fg: "#00C853" },
  yellow: { label: "ATENCIÓN",   bg: "rgba(255,214,0,0.22)", fg: "#B07A00" },
  red:    { label: "LLENO",      bg: "rgba(213,0,0,0.20)",   fg: "#D50000" },
};

function MiniBar({ pct, color }) {
  const safe = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div style={{ background: "var(--bg-2)", height: 6, borderRadius: 3, overflow: "hidden", border: "1px solid var(--border-2)" }}>
      <div style={{ width: `${safe}%`, height: "100%", background: color, transition: "width 200ms" }} />
    </div>
  );
}

export default function WarehouseCapacityPanel() {
  const [data, setData] = useState({ rp: null, rme: null, rsu: null });
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/warehouse-status/summary");
      setData(r.data);
      setLastRefresh(new Date());
      setError(false);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const allEmpty = (
    !data.rp || !data.rme || !data.rsu ||
    [data.rp, data.rme, data.rsu].every(
      (w) => !w?.total_capacity_volume && !w?.total_surface_m2 && !w?.responsible_name,
    )
  );

  return (
    <aside
      data-testid="warehouse-capacity-panel"
      style={{
        width: 220, flex: "0 0 220px", minWidth: 220,
        background: "var(--bg-1)", borderLeft: "1px solid var(--border-2)",
        padding: 12, height: "100%", overflowY: "auto",
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <div>
          <div style={{ fontWeight: 700, fontSize: 13 }}>Capacidad de Almacenes</div>
          <div className="text-xs text-dim">Tiempo real</div>
        </div>
        <button
          className="btn btn-sm"
          onClick={load}
          title="Recargar"
          data-testid="whcp-refresh"
          style={{ padding: "4px 6px" }}
        >
          <RefreshCw size={12} className={loading ? "spin" : ""}/>
        </button>
      </div>
      {lastRefresh && (
        <div className="text-xs text-dim mb-2" data-testid="whcp-last-refresh">
          Act. {lastRefresh.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
        </div>
      )}

      {error && (
        <div className="text-xs text-dim mt-2">Sin datos disponibles.</div>
      )}

      {!error && loading && !lastRefresh && (
        <>
          {[1, 2, 3].map((i) => (
            <div key={i} className="mt-3">
              <div style={{ background: "var(--bg-2)", height: 18, width: 70, borderRadius: 4 }} />
              <div style={{ background: "var(--bg-2)", height: 6, marginTop: 6, borderRadius: 3 }} />
              <div style={{ background: "var(--bg-2)", height: 6, marginTop: 4, borderRadius: 3 }} />
            </div>
          ))}
        </>
      )}

      {!error && !loading && allEmpty && (
        <div className="text-xs text-dim mt-2">
          Sin datos de capacidad. Configura los almacenes en Gestión de Residuos → Estatus de Almacenes.
        </div>
      )}

      {!error && !allEmpty && ["rp", "rme", "rsu"].map((key, idx) => {
        const w = data[key];
        if (!w) return null;
        const meta = TYPE_META[key];
        const s = STATUS[w.status_color] || STATUS.green;
        return (
          <div key={key} data-testid={`whcp-${key}`} style={{ marginTop: idx === 0 ? 8 : 12, paddingTop: idx === 0 ? 0 : 8, borderTop: idx === 0 ? 0 : "1px dashed var(--border-2)" }}>
            <div className="flex justify-between items-center">
              <span style={{ background: meta.color, color: "white", padding: "1px 6px", borderRadius: 3, fontWeight: 600, fontSize: 11 }} title={meta.full}>{meta.label}</span>
              <span style={{ background: s.bg, color: s.fg, border: `1px solid ${s.fg}55`, padding: "1px 6px", borderRadius: 3, fontSize: 10, fontWeight: 600 }}>{s.label}</span>
            </div>
            {w.alert_active && (
              <div className="flex items-center gap-1 mt-1" style={{ color: "#D50000", fontSize: 11 }}
                title={`Verificar antes de agendar una cita de recepción. Capacidad de volumen al ${w.occupied_volume_pct}%.`}
                data-testid={`whcp-alert-${key}`}
              >
                <AlertTriangle size={12}/> Capacidad al límite
              </div>
            )}
            <div className="mt-2">
              <MiniBar pct={w.occupied_volume_pct} color={s.fg} />
              <div className="text-xs text-dim mt-1">
                {w.occupied_volume_pct}% vol · {w.available_volume.toFixed(1)} {w.volume_unit} libres
              </div>
            </div>
            <div className="mt-1">
              <MiniBar pct={w.occupied_surface_pct} color={s.fg} />
              <div className="text-xs text-dim mt-1">
                {w.occupied_surface_pct}% sup · {w.available_surface_m2} m² libres
              </div>
            </div>
            {w.responsible_name && (
              <div className="text-xs text-dim mt-1">Resp: {w.responsible_name}</div>
            )}
          </div>
        );
      })}
    </aside>
  );
}
